import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { from } from 'rxjs';

import { FormValidators } from '../../core/forms/form-validators';
import { SmartForm } from '../../core/forms/smart-form';
import { SmartFormService } from '../../core/forms/smart-form.service';
import {
  ActivityCreateInput,
  ActivityLink,
  ActivityParticipant,
  ActivityRecord,
  ActivityService,
  ActivityStatus,
  ActivityType,
} from '../../core/services/activity.service';
import { AuthService } from '../../core/services/auth.service';
import { ContactsDirectoryService, UnifiedContact } from '../../core/services/contacts-directory.service';
import { TimezoneService } from '../../core/services/timezone.service';
import { AutocompleteOption } from '../inputs/autocomplete-select/autocomplete-select.component';

interface ActivitySchedulerFormValue {
  title: string;
  description: string;
  activity_type: ActivityType;
  status: ActivityStatus;
  start_local: string;
  end_local: string;
  timezone: string;
  all_day: boolean;
  location: string;
  recurrence_rule: string;
  participant_user_ids: string[];
}

interface ActivityLinkPayload {
  app_name: string;
  record_type: string;
  record_id: string;
  relationship_type: string;
}

@Component({
  selector: 'app-shared-activity-scheduler-modal',
  standalone: false,
  templateUrl: './activity-scheduler-modal.html',
  styleUrl: './activity-scheduler-modal.scss',
})
export class SharedActivitySchedulerModal implements OnInit, OnChanges {
  @Input() open = false;
  @Input() heading = 'Schedule Activity';
  @Input() contextLabel = 'Shared activity';

  @Input() linkAppName = 'crm';
  @Input() linkRecordType = '';
  @Input() linkRecordId = '';
  @Input() linkRelationshipType = 'related';

  @Input() presetType: ActivityType = 'meeting';
  @Input() presetTitle = '';
  @Input() presetStartLocal = '';
  @Input() presetEndLocal = '';

  @Input() activity: ActivityRecord | null = null;

  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<ActivityRecord>();

  readonly activityTypeOptions: Array<{ value: ActivityType; label: string }> = [
    { value: 'task', label: 'Task' },
    { value: 'call', label: 'Call' },
    { value: 'meeting', label: 'Meeting' },
    { value: 'reminder', label: 'Reminder' },
    { value: 'follow_up', label: 'Follow-up' },
    { value: 'shift', label: 'Shift' },
    { value: 'campaign', label: 'Campaign Activity' },
    { value: 'email', label: 'Email' },
  ];

  readonly statusOptions: Array<{ value: ActivityStatus; label: string }> = [
    { value: 'draft', label: 'Draft' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'done', label: 'Done' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'missed', label: 'Missed' },
  ];

  form: SmartForm<ActivitySchedulerFormValue>;

  participantOptions: AutocompleteOption[] = [];
  loadingParticipants = false;

  private currentUserId = '';

  constructor(
    private readonly smartFormService: SmartFormService,
    private readonly activityService: ActivityService,
    private readonly contactsDirectoryService: ContactsDirectoryService,
    private readonly authService: AuthService,
    private readonly timezoneService: TimezoneService
  ) {
    this.form = this.smartFormService.create<ActivitySchedulerFormValue>(
      {
        title: [
          '',
          FormValidators.compose(
            FormValidators.required('Title'),
            FormValidators.minLength(2, 'Title'),
            FormValidators.maxLength(160, 'Title')
          ),
        ],
        description: ['', FormValidators.maxLength(2000, 'Description')],
        activity_type: ['meeting', FormValidators.required('Activity type')],
        status: ['scheduled', FormValidators.required('Status')],
        start_local: ['', FormValidators.required('Start date and time')],
        end_local: ['', FormValidators.required('End date and time')],
        timezone: [this.timezoneService.getBrowserTimezone(), FormValidators.required('Timezone')],
        all_day: [false],
        location: ['', FormValidators.maxLength(200, 'Location')],
        recurrence_rule: ['', FormValidators.maxLength(500, 'Recurrence rule')],
        participant_user_ids: [[]],
      },
      (value) => from(this.createOrUpdateActivity(value)),
      (activity) => {
        if (activity) {
          this.saved.emit(activity);
        }
        this.handleClose();
      },
      undefined,
      {
        invalidMessage: 'Please complete required fields before saving activity.',
      }
    );

    this.form.setLabels({
      title: 'Title',
      activity_type: 'Activity Type',
      status: 'Status',
      start_local: 'Start Date/Time',
      end_local: 'End Date/Time',
      timezone: 'Timezone',
      participant_user_ids: 'Participants',
      location: 'Location',
      recurrence_rule: 'Recurrence Rule',
    });
  }

  async ngOnInit(): Promise<void> {
    await this.loadCurrentUser();
    await this.loadParticipantOptions();
    this.resetForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const openChanged = !!changes['open'];
    const activityChanged = !!changes['activity'];
    const presetChanged = !!changes['presetType'] || !!changes['presetTitle'] || !!changes['presetStartLocal'] || !!changes['presetEndLocal'];

    if (this.open && (openChanged || activityChanged || presetChanged)) {
      this.resetForm();
    }
  }

  get isEditing(): boolean {
    return !!this.activity?.id;
  }

  submit(): void {
    this.form.submit();
  }

  handleClose(): void {
    this.closed.emit();
  }

  searchParticipantOptions(query: string): AutocompleteOption[] {
    const normalized = query.trim().toLowerCase();
    if (!normalized) {
      return this.participantOptions;
    }

    return this.participantOptions.filter((option) => option.label.toLowerCase().includes(normalized));
  }

  private async createOrUpdateActivity(value: ActivitySchedulerFormValue): Promise<ActivityRecord | null> {
    const timezone = this.timezoneService.validateTimezone(value.timezone);
    const startLocal = this.normalizeLocalDateTime(value.start_local) || this.defaultStartLocal();

    let endLocal = this.normalizeLocalDateTime(value.end_local);
    if (!endLocal) {
      endLocal = this.defaultEndLocal(startLocal);
    }
    if (endLocal < startLocal) {
      endLocal = startLocal;
    }

    const selectedUserIds = this.normalizeParticipantSelection(value.participant_user_ids);

    const participantPayload: NonNullable<ActivityCreateInput['participants']> = selectedUserIds.map((userId) => ({
      user_id: userId,
      participant_role: userId === this.currentUserId ? 'owner' : 'attendee',
      response_status: userId === this.currentUserId ? 'accepted' : 'pending',
    }));

    const linkPayload = this.resolveLinkPayload();

    const basePayload = {
      title: value.title.trim(),
      description: this.nullableText(value.description),
      activity_type: value.activity_type,
      status: value.status,
      start_time_local: startLocal,
      end_time_local: endLocal,
      timezone,
      all_day: value.all_day,
      recurrence_rule: this.nullableText(value.recurrence_rule),
      location: this.nullableText(value.location),
    };

    if (this.activity?.id) {
      await this.activityService.updateActivity(this.activity.id, basePayload);

      await this.syncParticipants(this.activity.id, this.activity.participants, selectedUserIds);
      await this.syncLinks(this.activity.id, this.activity.links, linkPayload);

      return this.activityService.getActivity(this.activity.id);
    }

    const payload: ActivityCreateInput = {
      ...basePayload,
      title: basePayload.title,
      activity_type: basePayload.activity_type,
      participants: participantPayload,
      links: linkPayload ? [linkPayload] : undefined,
    };

    return this.activityService.createActivity(payload);
  }

  private async loadCurrentUser(): Promise<void> {
    try {
      const me: any = await this.authService.getUser();
      this.currentUserId = this.asText(me?.id) || this.asText(me?.user_id) || this.asText(me?.user?.id);
    } catch {
      this.currentUserId = '';
    }
  }

  private async loadParticipantOptions(): Promise<void> {
    this.loadingParticipants = true;

    try {
      const directory = await this.contactsDirectoryService.getDirectory();
      const byUser = new Map<string, AutocompleteOption>();

      for (const contact of directory.contacts || []) {
        const userId = this.asText(contact.user_id);
        if (!userId) {
          continue;
        }

        if (!byUser.has(userId)) {
          byUser.set(userId, {
            value: userId,
            label: this.participantLabel(contact),
          });
        }
      }

      this.participantOptions = Array.from(byUser.values()).sort((left, right) =>
        left.label.localeCompare(right.label)
      );
    } catch {
      this.participantOptions = [];
    } finally {
      this.loadingParticipants = false;
    }
  }

  private resetForm(): void {
    const activity = this.activity;

    if (activity?.id) {
      const timezone = this.timezoneService.validateTimezone(activity.timezone || this.timezoneService.getBrowserTimezone());
      const startLocal = this.toLocalInput(activity.start_time_utc, timezone) || this.defaultStartLocal();
      const endLocal = this.toLocalInput(activity.end_time_utc, timezone) || this.defaultEndLocal(startLocal);

      this.form.form.reset(
        {
          title: activity.title,
          description: activity.description || '',
          activity_type: activity.activity_type,
          status: activity.status,
          start_local: startLocal,
          end_local: endLocal,
          timezone,
          all_day: activity.all_day,
          location: activity.location || '',
          recurrence_rule: activity.recurrence_rule || '',
          participant_user_ids: this.extractParticipantUserIds(activity.participants),
        },
        { emitEvent: false }
      );

      return;
    }

    const timezone = this.timezoneService.getBrowserTimezone();
    const startLocal = this.normalizeLocalDateTime(this.presetStartLocal) || this.defaultStartLocal();
    const endLocal = this.normalizeLocalDateTime(this.presetEndLocal) || this.defaultEndLocal(startLocal);

    this.form.form.reset(
      {
        title: this.presetTitle || this.defaultTitle(),
        description: '',
        activity_type: this.presetType,
        status: 'scheduled',
        start_local: startLocal,
        end_local: endLocal,
        timezone,
        all_day: false,
        location: '',
        recurrence_rule: '',
        participant_user_ids: this.currentUserId ? [this.currentUserId] : [],
      },
      { emitEvent: false }
    );
  }

  private resolveLinkPayload(): ActivityLinkPayload | undefined {
    const appName = this.asText(this.linkAppName);
    const recordType = this.asText(this.linkRecordType);
    const recordId = this.asText(this.linkRecordId);

    if (!appName || !recordType || !recordId) {
      return undefined;
    }

    return {
      app_name: appName,
      record_type: recordType,
      record_id: recordId,
      relationship_type: this.asText(this.linkRelationshipType) || 'related',
    };
  }

  private async syncParticipants(
    activityId: string,
    existingParticipants: ActivityParticipant[],
    selectedUserIds: string[]
  ): Promise<void> {
    const existingByUser = new Map<string, ActivityParticipant>();

    for (const participant of existingParticipants) {
      const userId = this.asText(participant.user_id);
      if (!userId) {
        continue;
      }

      existingByUser.set(userId, participant);
    }

    const selectedSet = new Set(selectedUserIds);

    for (const [userId, participant] of existingByUser.entries()) {
      if (!selectedSet.has(userId)) {
        await this.activityService.removeParticipant(activityId, participant.id);
      }
    }

    for (const userId of selectedUserIds) {
      if (existingByUser.has(userId)) {
        const existing = existingByUser.get(userId)!;
        const desiredRole = userId === this.currentUserId ? 'owner' : 'attendee';
        const desiredStatus = userId === this.currentUserId ? 'accepted' : 'pending';

        if (existing.participant_role !== desiredRole || existing.response_status !== desiredStatus) {
          await this.activityService.updateParticipant(activityId, existing.id, {
            response_status: desiredStatus,
            participant_role: desiredRole,
          });
        }

        continue;
      }

      await this.activityService.addParticipant(activityId, {
        user_id: userId,
        participant_role: userId === this.currentUserId ? 'owner' : 'attendee',
        response_status: userId === this.currentUserId ? 'accepted' : 'pending',
      });
    }
  }

  private async syncLinks(
    activityId: string,
    existingLinks: ActivityLink[],
    desiredLink?: ActivityLinkPayload
  ): Promise<void> {
    const desired = desiredLink
      ? `${desiredLink.app_name}|${desiredLink.record_type}|${desiredLink.record_id}|${desiredLink.relationship_type || 'related'}`
      : '';

    if (!desiredLink) {
      return;
    }

    const existingMatch = existingLinks.find((link) => {
      const current = `${link.app_name}|${link.record_type}|${link.record_id}|${link.relationship_type || 'related'}`;
      return current === desired;
    });

    if (existingMatch) {
      return;
    }

    const sameRecordLinks = existingLinks.filter(
      (link) =>
        link.app_name === desiredLink.app_name &&
        link.record_type === desiredLink.record_type &&
        link.record_id === desiredLink.record_id
    );

    if (sameRecordLinks.length === 0) {
      await this.activityService.linkActivity(activityId, desiredLink);
      return;
    }

    for (const link of sameRecordLinks) {
      if (link.relationship_type !== desiredLink.relationship_type) {
        await this.activityService.unlinkActivity(activityId, link.id);
      }
    }

    await this.activityService.linkActivity(activityId, desiredLink);
  }

  private normalizeParticipantSelection(values: string[]): string[] {
    const selected = Array.isArray(values) ? values : [];
    const ids = new Set<string>();

    for (const value of selected) {
      const userId = this.asText(value);
      if (userId) {
        ids.add(userId);
      }
    }

    if (this.currentUserId) {
      ids.add(this.currentUserId);
    }

    return Array.from(ids);
  }

  private extractParticipantUserIds(participants: ActivityParticipant[]): string[] {
    const values = new Set<string>();

    for (const participant of participants || []) {
      const userId = this.asText(participant.user_id);
      if (userId) {
        values.add(userId);
      }
    }

    if (this.currentUserId) {
      values.add(this.currentUserId);
    }

    return Array.from(values);
  }

  private participantLabel(contact: UnifiedContact): string {
    const displayName = this.asText(contact.display_name) || 'User';
    const email = this.asText(contact.email);

    if (!email) {
      return displayName;
    }

    return `${displayName} (${email})`;
  }

  private toLocalInput(utcIso: string | null | undefined, timezone: string): string {
    const normalized = this.asText(utcIso);
    if (!normalized) {
      return '';
    }

    return this.timezoneService.utcIsoToLocalInput(normalized, timezone);
  }

  private defaultTitle(): string {
    return `${this.activityTypeLabel(this.presetType)} for ${this.contextLabel}`;
  }

  private activityTypeLabel(value: ActivityType): string {
    return this.activityTypeOptions.find((option) => option.value === value)?.label || 'Activity';
  }

  private normalizeLocalDateTime(value: string): string {
    const text = this.asText(value);
    return text ? text.slice(0, 16) : '';
  }

  private defaultStartLocal(): string {
    const now = new Date();
    now.setSeconds(0, 0);

    const minutes = now.getMinutes();
    const rounded = minutes <= 30 ? 30 : 60;
    now.setMinutes(rounded);

    if (rounded === 60) {
      now.setHours(now.getHours() + 1, 0, 0, 0);
    }

    return this.datetimeLocalFromDate(now);
  }

  private defaultEndLocal(startLocal: string): string {
    const parsed = new Date(startLocal);
    if (Number.isNaN(parsed.getTime())) {
      const fallback = new Date();
      fallback.setHours(fallback.getHours() + 1);
      return this.datetimeLocalFromDate(fallback);
    }

    parsed.setHours(parsed.getHours() + 1);
    return this.datetimeLocalFromDate(parsed);
  }

  private datetimeLocalFromDate(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    const hour = `${date.getHours()}`.padStart(2, '0');
    const minute = `${date.getMinutes()}`.padStart(2, '0');

    return `${year}-${month}-${day}T${hour}:${minute}`;
  }

  private nullableText(value: unknown): string | null {
    const text = this.asText(value);
    return text || null;
  }

  private asText(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }
}
